/**
 * Chat + profiles backend adapter.
 *
 * If VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set, messages and
 * profiles are shared between all visitors via Supabase REST (polling).
 * Otherwise everything falls back to localStorage — single-browser demo mode.
 * See supabase/schema.sql for the two tables.
 */

export type Profile = {
  address: string;
  username: string;
  avatarUrl: string;
  bio: string;
  xHandle: string;
  /** true when the handle came back from a real X OAuth login */
  xVerified: boolean;
  updatedAt: number;
};

export type ChatMessage = {
  id: string;
  address: string;
  text: string;
  createdAt: number;
};

export type RoundRecord = {
  roundId: bigint;
  resultIndex: number;
  resultName: string;
  resultHex: string;
  totalStaked: bigint;
  winPool: bigint;
  players: number;
  entropy: string;
  entropyBlock?: bigint;
};

export type BetRecord = {
  roundId: bigint;
  address: string;
  colorIndex: number;
  amount: bigint;
};

export type LeaderboardRow = {
  address: string;
  displayName: string;
  avatarUrl: string;
  xHandle: string;
  xVerified: boolean;
  roundsPlayed: number;
  betsWon: number;
  totalStaked: bigint;
  totalWon: bigint;
  netProfit: bigint;
};

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
const SUPABASE_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? "";

export const chatIsShared = Boolean(SUPABASE_URL && SUPABASE_KEY);

const MSG_KEY = "wheel-chat-messages";
const PROFILE_KEY = "wheel-profiles";
const MAX_MESSAGES = 100;

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: `Bearer ${SUPABASE_KEY}`,
    "Content-Type": "application/json",
  };
}

function loadLocal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveLocal(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable
  }
}

export async function fetchMessages(): Promise<ChatMessage[]> {
  if (!chatIsShared) {
    return loadLocal<ChatMessage[]>(MSG_KEY, []);
  }
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/wheel_messages?select=*&order=created_at.desc&limit=${MAX_MESSAGES}`,
    { headers: headers() }
  );
  if (!res.ok) throw new Error(`chat fetch failed: ${res.status}`);
  const rows = (await res.json()) as {
    id: string;
    address: string;
    text: string;
    created_at: string;
  }[];
  return rows
    .map((r) => ({
      id: r.id,
      address: r.address,
      text: r.text,
      createdAt: new Date(r.created_at).getTime(),
    }))
    .reverse();
}

export async function sendMessage(address: string, text: string): Promise<void> {
  const trimmed = text.trim().slice(0, 280);
  if (!trimmed) return;
  if (!chatIsShared) {
    const msgs = loadLocal<ChatMessage[]>(MSG_KEY, []);
    msgs.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      address,
      text: trimmed,
      createdAt: Date.now(),
    });
    saveLocal(MSG_KEY, msgs.slice(-MAX_MESSAGES));
    return;
  }
  const res = await fetch(`${SUPABASE_URL}/rest/v1/wheel_messages`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ address, text: trimmed }),
  });
  if (!res.ok) throw new Error(`chat send failed: ${res.status}`);
}

export async function fetchProfiles(): Promise<Record<string, Profile>> {
  if (!chatIsShared) {
    return loadLocal<Record<string, Profile>>(PROFILE_KEY, {});
  }
  const res = await fetch(`${SUPABASE_URL}/rest/v1/wheel_profiles?select=*`, {
    headers: headers(),
  });
  if (!res.ok) throw new Error(`profiles fetch failed: ${res.status}`);
  const rows = (await res.json()) as {
    address: string;
    username: string;
    avatar_url: string;
    bio: string;
    x_handle: string;
    x_verified: boolean;
    updated_at: string;
  }[];
  const map: Record<string, Profile> = {};
  for (const r of rows) {
    map[r.address.toLowerCase()] = {
      address: r.address,
      username: r.username ?? "",
      avatarUrl: r.avatar_url ?? "",
      bio: r.bio ?? "",
      xHandle: r.x_handle ?? "",
      xVerified: Boolean(r.x_verified),
      updatedAt: new Date(r.updated_at).getTime(),
    };
  }
  return map;
}

/** Thrown when a username is already claimed by another wallet. */
export class UsernameTakenError extends Error {
  constructor(username: string) {
    super(`"${username}" is already taken`);
    this.name = "UsernameTakenError";
  }
}

/**
 * True when another address already uses this name (case-insensitive).
 * The unique index in the database is the real guard; this is for fast feedback.
 */
export async function isUsernameTaken(username: string, selfAddress: string): Promise<boolean> {
  const name = username.trim().toLowerCase();
  if (!name) return false;
  const self = selfAddress.toLowerCase();
  const profiles = await fetchProfiles();
  return Object.values(profiles).some(
    (p) => p.username.trim().toLowerCase() === name && p.address.toLowerCase() !== self
  );
}

export async function saveProfile(profile: Profile): Promise<void> {
  const addr = profile.address.toLowerCase();
  const name = profile.username.trim();

  if (name && (await isUsernameTaken(name, addr))) {
    throw new UsernameTakenError(name);
  }

  if (!chatIsShared) {
    const map = loadLocal<Record<string, Profile>>(PROFILE_KEY, {});
    map[addr] = { ...profile, updatedAt: Date.now() };
    saveLocal(PROFILE_KEY, map);
    return;
  }
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/wheel_profiles?on_conflict=address`,
    {
      method: "POST",
      headers: { ...headers(), Prefer: "resolution=merge-duplicates" },
      body: JSON.stringify({
        address: addr,
        username: profile.username.slice(0, 24),
        avatar_url: profile.avatarUrl,
        bio: profile.bio.slice(0, 160),
        x_handle: profile.xHandle.replace(/^@/, "").slice(0, 32),
        x_verified: profile.xVerified,
        updated_at: new Date().toISOString(),
      }),
    }
  );
  // 409 = the unique index rejected the name in a race with another wallet.
  if (res.status === 409) throw new UsernameTakenError(name);
  if (!res.ok) throw new Error(`profile save failed: ${res.status}`);
}

/**
 * Index a settled round. Safe to call from every player's browser — the
 * primary key on round_id makes duplicate reports a no-op.
 */
export async function recordRound(round: RoundRecord): Promise<void> {
  if (!chatIsShared) return;
  await fetch(`${SUPABASE_URL}/rest/v1/wheel_rounds?on_conflict=round_id`, {
    method: "POST",
    headers: { ...headers(), Prefer: "resolution=ignore-duplicates" },
    body: JSON.stringify({
      round_id: round.roundId.toString(),
      result_index: round.resultIndex,
      result_name: round.resultName,
      result_hex: round.resultHex,
      total_staked: round.totalStaked.toString(),
      win_pool: round.winPool.toString(),
      total_payout: (round.winPool * 10n).toString(),
      players: round.players,
      entropy: round.entropy,
      entropy_block: round.entropyBlock?.toString() ?? null,
    }),
  }).catch(() => undefined);
}

/** Index one player's per-color stakes for a round. */
export async function recordBets(
  bets: BetRecord[],
  resultIndex?: number
): Promise<void> {
  if (!chatIsShared || bets.length === 0) return;
  await fetch(`${SUPABASE_URL}/rest/v1/wheel_bets?on_conflict=round_id,address,color_index`, {
    method: "POST",
    headers: { ...headers(), Prefer: "resolution=merge-duplicates" },
    body: JSON.stringify(
      bets.map((b) => {
        const won = resultIndex !== undefined && b.colorIndex === resultIndex;
        return {
          round_id: b.roundId.toString(),
          address: b.address.toLowerCase(),
          color_index: b.colorIndex,
          amount: b.amount.toString(),
          won,
          payout: won ? (b.amount * 10n).toString() : "0",
        };
      })
    ),
  }).catch(() => undefined);
}

/** Top players by net profit. Empty when the database isn't configured. */
export async function fetchLeaderboard(limit = 20): Promise<LeaderboardRow[]> {
  if (!chatIsShared) return [];
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/wheel_leaderboard?select=*&order=net_profit.desc&limit=${limit}`,
    { headers: headers() }
  );
  if (!res.ok) return [];
  const rows = (await res.json()) as Record<string, string | number | boolean | null>[];
  return rows.map((r) => ({
    address: String(r.address ?? ""),
    displayName: String(r.display_name ?? ""),
    avatarUrl: String(r.avatar_url ?? ""),
    xHandle: String(r.x_handle ?? ""),
    xVerified: Boolean(r.x_verified),
    roundsPlayed: Number(r.rounds_played ?? 0),
    betsWon: Number(r.bets_won ?? 0),
    totalStaked: BigInt(String(r.total_staked ?? "0")),
    totalWon: BigInt(String(r.total_won ?? "0")),
    netProfit: BigInt(String(r.net_profit ?? "0")),
  }));
}
